import { SETTINGS } from '@Config';
import { Logger, LoggerService } from '@Shared/logger/index';
import { DataUtil, DomainUtil } from '@Shared/utils';
import { PluginClass } from './plugin';
import { MNGAuthentication } from '@Core';
import './styles/index.scss';
import { AuthenticationUtils, AuthModule } from '@Modules/auth_module';
import { EntitlementService } from '@Modules/entitlement_module';
import { OneTapModule } from '@Modules/onetap_module';
import { PageSuiteService } from '@Modules/pagesuite_module';
import { UIModule } from '@Modules/ui_module';
import { Blueconic } from '@Modules/blueconic_module';
import { Features } from '@Modules/features_module';
import { Helpers } from './helpers';

const Log: Logger = LoggerService.createLogger('Entry');
Log.trace('MG2 Auth Plugin Loaded');
let pluginInitialed = false;

window.MNGAuth = {
  init,
  setLoggerEnabled,
  setLogLevel,
  logout,
  isFrontPage,
  checkEntitlements,
  getEntitlements,
  showScreen,
  promptOneTap,
  Blueconic,
  Features,
  Helpers,
};

// Expose logout for debugging via the browser console.
if (typeof window !== 'undefined') {
  AuthenticationUtils.initializeMNGAuthenticationGlobal();
  window.MNGAuthentication!.customLogout = logout;
  //window.MNGAuthentication!.getUserInfo = getUserInfo;
  bindLogoutClickHandler();
}
start();

declare global {
  interface Window {
    MG2Loader: { [method: string]: (...args: any[]) => any };
    MNGAuthentication?: MNGAuthentication;
    dataLayer?: Record<string, any>[];
    MG2DL?: Record<string, any>[];
    apple_config?: {
      sourceId: string;
      applePayEnabled: boolean;
      loggedOutPurchaseEnabled: boolean
    };
  }
}

function onDOMReady(fn: () => void): void {
  if (document.readyState === 'loading') {
    // Still loading — wait for the event
    document.addEventListener('DOMContentLoaded', fn);
  } else {
    // `interactive` or `complete` — DOM is already ready
    fn();
  }
}

async function getUserInfo(): Promise<any> {
  return {userId: '', email: ''};
  //return AuthModule.getUserInfo();
}

function init(options?: any): void {
  if (pluginInitialed) {
    Log.trace('Plugin Already Initialized');
    return;
  }

  const plugin = new PluginClass();
  plugin.init(options);
  Log.trace('Plugin Initialized');
  pluginInitialed = true;
}

function setLoggerEnabled(val: boolean): void {
  Log.info('setLoggerEnabled');
  LoggerService.setEnabled(val);
}

function setLogLevel(val: string): void {
  Log.info('setLogLevel');
  LoggerService.setLevel(val);
}

// Expose the customLogout flow as the main logout
async function logout(): Promise<void> {
  await AuthModule.logout();
}

function isFrontPage(): boolean {
  const isFront = PageSuiteService.Instance.IsFront;
  Log.trace('isFrontPage', isFront);
  return isFront;
}

async function checkEntitlements(): Promise<void> {
  Log.trace('Checking Entitlements....');
  const result = await EntitlementService.Instance.checkEntitlements();
  Log.trace('Entitlements Check Result', result);
}

function getEntitlements(): any {
  const mngSession = EntitlementService.Instance.getMNGSession();
  Log.trace('getEntitlements', mngSession);

  return mngSession;
}

function showScreen(name): void {
  UIModule.ScreenManager.showScreen(name);
}

function promptOneTap(): Promise<void> {
  return OneTapModule.prompt();
}

//internal method that is used to check if we have an autoInit param so we can manually call init method.
function start() {
  //check if we have autoInit params...
  // @ts-expect-error Testing
  const autoInit = DomainUtil.getUrlParamFromScriptTag('mng-auth', 'autoInit') || process?.env?.TEST == 'true';
  if (autoInit) {
    onDOMReady(() => {
      Log.trace('AutoInit is set to true, so we will init the plugin.');
      //we should auto init so we also need to load CSS since we would not just have the site add the css file unless we can't manually call init.
      // Load the plugin CSS, but don't let a failed CSS stop the plugin from initializing
      DataUtil.loadCSS(SETTINGS.awsBaseUrl + 'mng-auth.css')
        .then(() => {
          Log.trace('CSS Loaded');
        })
        .catch((err) => {
          Log.error('Error loading CSS', err);
        })
        .finally(() => {
          if (document.body) {
            void document.body.offsetHeight;
          }
          init();
        });
    });
  }
}

// Host themes render the logout button as a nested trio:
//   .digisubs-logout-content > .digisubs-logout > .digisubs-logout-text
// and style the OUTER wrapper as the full-width clickable row, so most clicks
// land on .digisubs-logout-content itself. closest() only walks up, so matching
// .digisubs-logout alone missed every click that wasn't directly on the inner
// element — the button looked dead unless you hit the text.
const LOGOUT_TRIGGER_SELECTOR = '.digisubs-logout, .digisubs-logout-content';

// Delegated click handler: any element matching LOGOUT_TRIGGER_SELECTOR
// (or nested inside one) triggers the customLogout flow when clicked.
function bindLogoutClickHandler(): void {
  // Capture phase: themes commonly call stopPropagation() on clicks inside
  // dropdown/off-canvas menus, which would keep a bubble-phase listener on
  // document from ever running. Capture fires before any of those handlers.
  document.addEventListener(
    'click',
    (event: MouseEvent) => {
      const target = event.target as Element | null;
      const trigger = target?.closest?.(LOGOUT_TRIGGER_SELECTOR);
      if (!trigger) {
        return;
      }
      event.preventDefault();
      Log.trace('digisubs-logout clicked, calling customLogout');
      // customLogout is normally the real logout, but fall back to MNGAuth.logout
      // in case something on the page replaced the MNGAuthentication global.
      const runLogout =
        window.MNGAuthentication?.customLogout ?? window.MNGAuth?.logout;
      if (typeof runLogout !== 'function') {
        Log.error('No logout function available on window');
        return;
      }
      // logout is async; surface failures instead of letting the rejection vanish.
      Promise.resolve(runLogout()).catch((err) => {
        Log.error('customLogout failed', err);
      });
    },
    true,
  );
}
